import { forwardRef, ReactNode, Ref } from 'react';

import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';

import { useAnalytics } from 'src/components/settings/analytics';
import { Repository } from 'src/components/types';
import { ClientLinkWrapper } from 'src/router/components';
import {
  getRepoOwnerName,
  getRepoOwnerUrl,
} from 'src/sections/repository/utils/repository-owner';
import WorkspaceIcon from 'src/sections/workspace/components/workspace-icon';
import { urls as globalUrls } from 'src/urls';

import * as styles from './breadcrumbs.style';

type RepositoryBreadcrumbsProps = {
  children?: ReactNode;
  isRoot?: boolean;
  repository: Repository | null | undefined;
};

const RepositoryBreadcrumbs = ({
  repository,
  isRoot = false,
  children,
}: RepositoryBreadcrumbsProps) => {
  const { publishBreadcrumbClickEvent } = useAnalytics();

  const projectUrl = () => {
    if (!repository) {
      return undefined;
    }

    const { owner, project } = repository;

    if (!project) {
      return undefined;
    }

    if (project.links) {
      return project.links.html.href;
    }

    return owner ? globalUrls.ui.project(owner.uuid, project.key) : undefined;
  };

  if (!repository) {
    return null;
  }
  const { project, slug } = repository;

  const LinkComponent = forwardRef(function BreadcrumbsItemRepositoryLink(
    // explicitly don't pass through props
    _props,
    ref: Ref<HTMLAnchorElement>
  ) {
    return (
      <styles.BreadcrumbsItemRepositoryLink
        // @ts-ignore Emotion's types are a bit wonky
        ref={ref}
        onClick={() => publishBreadcrumbClickEvent('RepositoryLink')}
        repository={repository}
      />
    );
  });

  const workspaceIcon = <WorkspaceIcon workspace={repository.workspace} />;
  const projectLink = projectUrl();
  const workspaceLink = getRepoOwnerUrl(repository);

  return (
    <Breadcrumbs>
      <ClientLinkWrapper url={workspaceLink}>
        <BreadcrumbsItem
          href={workspaceLink}
          text={getRepoOwnerName(repository) || ''}
          onClick={_ => publishBreadcrumbClickEvent('WorkspaceLink')}
          iconBefore={workspaceIcon}
        />
      </ClientLinkWrapper>
      {project && (
        <ClientLinkWrapper url={projectLink}>
          <BreadcrumbsItem
            href={projectLink}
            text={project.name}
            onClick={_ => publishBreadcrumbClickEvent('ProjectLink')}
          />
        </ClientLinkWrapper>
      )}
      {slug && !isRoot && (
        // `text` is not actually used, but it's a required prop
        <BreadcrumbsItem component={LinkComponent} text={slug} />
      )}
      {children}
    </Breadcrumbs>
  );
};

// eslint-disable-next-line import/no-default-export
export default RepositoryBreadcrumbs;
